/* eslint-disable react/jsx-props-no-spreading */
// allowing props spreading due to native next components being used in this file
import { useState } from 'react';
import type { AppProps as NextAppProps } from 'next/app';
import App, { AppInitialProps as NextAppInitialProps } from 'next/app';
import { AppTreeType } from 'next/dist/shared/lib/utils';
import Head from 'next/head';
import { Router } from 'next/router';
import { NextComponentType, NextPageContext } from 'next/types';

import { config } from 'config';
import { GGTheme } from 'themes/types';

import { CoreFeaturesBackendData } from '@glossgenius/core-features-react-sdk';

import { useDatadogSessionIdentification } from 'hooks/useDatadogSessionIdentification';
import { useG4Pixel } from 'hooks/useG4Pixel';

import { Membership } from 'types/membership';
import { Package } from 'types/package';
import { TPublicUser } from 'types/publicUser';

import { initDataDog } from 'utils/initDatadog';
import { initMoment } from 'utils/initMoment';

import { AppProvider } from '../components/AppProviders';
import { DevThemeSwitcherWrapper } from '../components/DevThemeSwitcherWrapper';
import { GlobalStyles } from '../components/GlobalStyles';
import { useFbPixel } from '../hooks/useFbPixel';
import {
  getCommonServersideProps,
  hydrateCommonServersideProps,
} from '../utils/commonServersideProps';
import { getCommonMetaTags } from '../utils/getCommonMetaTags';
import routesRewrites from '../utils/routesRewrites';
import ErrorPage from './_error';

import 'normalize.css';
import '../components/variables.css';
import '../styles/globals.css';
import '../../public/assets/css/day-picker.css';
import '../../public/assets/css/common.css';
import '../../public/assets/css/Dropdown.css';
import 'remixicon/fonts/remixicon.css';

if (process.env.IS_MSW_ENABLED_FOR_SERVER === 'true') {
  require('../../int/msw/index');
}

interface AppProps extends NextAppProps {
  serverContext?: {
    publicUser: TPublicUser;
    packages: Package[];
    memberships: Membership[];
    browser: string;
    theme?: GGTheme;
    coreFeaturesBackendData: CoreFeaturesBackendData;
  };
  pageProps: NextAppInitialProps['pageProps'];
}

// Initialize DataDog
// https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/?tab=npm
initDataDog();

const MyApp = ({ Component, pageProps, serverContext }: AppProps) => {
  const [devThemeOverride, setDevThemeOverride] =
    useState<Partial<GGTheme> | null>(null);

  const hydratedServerContext = pageProps.err
    ? {}
    : hydrateCommonServersideProps(serverContext);

  const { publicUser, theme } = hydratedServerContext;

  const metaTags = getCommonMetaTags(pageProps.metadata, publicUser);

  useFbPixel(publicUser?.facebook_pixel_id);
  useG4Pixel(!!publicUser, publicUser?.ga4_measurement_id);
  useDatadogSessionIdentification({
    slug: publicUser?.slug,
    businessToken: publicUser?.business_token,
    guid: publicUser?.guid,
    themeName: theme?.name,
  });

  const themeOverride = pageProps?.themeOverride || {};

  const mergedTheme = {
    ...theme,
    ...themeOverride,
    ...(devThemeOverride || {}),
  };
  const updatedServerContext = { ...hydratedServerContext, theme: mergedTheme };

  return (
    <>
      <Head>
        <link rel="shortcut icon" href="/favicon.ico" />
        {config.staticAssetHostUrl && (
          <link rel="preconnect" href={config.staticAssetHostUrl} />
        )}
        {metaTags.map((metaTag) => metaTag)}
      </Head>
      {pageProps.err ? (
        <ErrorPage {...pageProps} />
      ) : (
        <AppProvider context={updatedServerContext}>
          <>
            <GlobalStyles theme={mergedTheme} />
            <Component {...pageProps} />
            <DevThemeSwitcherWrapper onThemeOverride={setDevThemeOverride} />
          </>
        </AppProvider>
      )}
    </>
  );
};

interface AppInitialProps extends NextAppInitialProps {
  serverContext?: any;
}

type AppContext = {
  Component: NextComponentType<NextPageContext, object, object>;
  AppTree: AppTreeType;
  ctx: NextPageContext;
  router: Router;
};

MyApp.getInitialProps = async (
  appContext: AppContext,
): Promise<AppInitialProps> => {
  const { req, res, query } = appContext.ctx;

  try {
    let serverContext;

    if (req && res && res.statusCode !== 404) {
      serverContext = await getCommonServersideProps(req, res, query);

      routesRewrites(appContext, serverContext);
      initMoment(serverContext?.publicUser?.preferences?.timezone);
    }
    // calls page's `getInitialProps` and fills `appProps.pageProps`
    const appProps = await App.getInitialProps(appContext);

    // If the page being rendered is an error page, skip fetching server context
    if (appProps.pageProps.err) {
      return { ...appProps };
    }

    return { ...appProps, serverContext };
  } catch (err: any) {
    if (err.name === 'SlugNotFound') {
      // redirect to LandingPage not-found
      res!.writeHead(301, {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        Location: `${config.rootUrl}`,
      });
      res!.end();
      return { pageProps: {} }; // Need to return to satisfy TS
    }

    if (err.response?.status) {
      res!.statusCode = err.response.status;
    }

    const pageProps = ErrorPage.getInitialProps({ req, res, err });

    return { pageProps };
  }
};

// eslint-disable-next-line import/no-default-export
export default MyApp;
